import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from '../utils/toast';
import { Users, TrendingUp, TrendingDown, Eye, Activity, CheckCircle2, Clock, AlertCircle, BookOpen } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReportingManagerDashboard = ({ isEmbedded = false }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [teamData, setTeamData] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberDetails, setMemberDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  
  useEffect(() => {
    fetchTeamData();
  }, []);
  
  const fetchTeamData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/manager/team-progress`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeamData(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('You are not assigned as a Reporting Manager');
        navigate('/staff/dashboard');
      } else {
        toast.error('Failed to fetch team data');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const fetchMemberDetails = async (userId) => {
    setDetailsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/manager/subordinate/${userId}/details`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMemberDetails(response.data);
      setShowDetailsModal(true);
    } catch (error) {
      toast.error('Failed to fetch member details');
    } finally {
      setDetailsLoading(false);
    }
  };
  
  const handleViewDetails = (member) => {
    setSelectedMember(member);
    fetchMemberDetails(member.user_id);
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };
  
  if (loading || !teamData) {
    const loadingContent = (
      <div className="flex items-center justify-center h-full py-20">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#0056B3] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading team data...</p>
        </div>
      </div>
    );
    
    return isEmbedded ? loadingContent : <AdminLayout>{loadingContent}</AdminLayout>;
  }
  
  const deptMetrics = teamData.department_metrics;
  
  const content = (
    <div className={isEmbedded ? "max-w-full overflow-x-hidden" : "p-3 sm:p-4 md:p-6 lg:p-8 max-w-full overflow-x-hidden"}>
        {/* Page Header */}
        <div className="mb-5 md:mb-8 min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 leading-tight break-words">TEAM PROGRESS DASHBOARD</h1>
          <p className="text-sm text-gray-600 mt-2 break-words">
            {teamData.manager_info.name} • {teamData.department_info.department_name}
          </p>
        </div>
        
        {/* Department Overview Metric Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-5 md:mb-8">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 sm:p-5 lg:p-6 shadow-lg hover:shadow-xl transition-shadow min-w-0">
            <div className="flex justify-between items-start">
              <div className="min-w-0">
                <p className="text-sm font-medium text-blue-700 mb-2">Team Members</p>
                <h3 className="text-3xl sm:text-4xl font-bold text-blue-900">{deptMetrics.total_team_members}</h3>
              </div>
              <div className="w-11 h-11 sm:w-12 sm:h-12 bg-blue-200 rounded-lg flex items-center justify-center shrink-0">
                <Users className="w-6 h-6 text-blue-700" />
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 sm:p-5 lg:p-6 shadow-lg hover:shadow-xl transition-shadow min-w-0">
            <div className="flex justify-between items-start">
              <div className="min-w-0">
                <p className="text-sm font-medium text-green-700 mb-2">Completed Topics</p>
                <h3 className="text-3xl sm:text-4xl font-bold text-green-900">{deptMetrics.total_completed}</h3>
              </div>
              <div className="w-11 h-11 sm:w-12 sm:h-12 bg-green-200 rounded-lg flex items-center justify-center shrink-0">
                <CheckCircle2 className="w-6 h-6 text-green-700" />
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl p-4 sm:p-5 lg:p-6 shadow-lg hover:shadow-xl transition-shadow min-w-0">
            <div className="flex justify-between items-start">
              <div className="min-w-0">
                <p className="text-sm font-medium text-yellow-700 mb-2">In Progress</p>
                <h3 className="text-3xl sm:text-4xl font-bold text-yellow-900">{deptMetrics.total_in_progress}</h3>
              </div>
              <div className="w-11 h-11 sm:w-12 sm:h-12 bg-yellow-200 rounded-lg flex items-center justify-center shrink-0">
                <Clock className="w-6 h-6 text-yellow-700" />
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 sm:p-5 lg:p-6 shadow-lg hover:shadow-xl transition-shadow min-w-0">
            <div className="flex justify-between items-start">
              <div className="min-w-0">
                <p className="text-sm font-medium text-purple-700 mb-2">Avg Completion</p>
                <h3 className="text-3xl sm:text-4xl font-bold text-purple-900">{deptMetrics.avg_team_completion}%</h3>
              </div>
              <div className="w-11 h-11 sm:w-12 sm:h-12 bg-purple-200 rounded-lg flex items-center justify-center shrink-0">
                <TrendingUp className="w-6 h-6 text-purple-700" />
              </div>
            </div>
          </div>
        </div>
        
        {/* Team Progress Table (Individual Team Member Progress) - MOVED TO TOP */}
        <div className="bg-white rounded-xl shadow-md p-4 sm:p-5 lg:p-6 mb-5 md:mb-8 max-w-full overflow-hidden">
          <div className="flex items-center gap-3 mb-4 md:mb-6 min-w-0">
            <TrendingUp className="w-6 h-6 text-[#0056B3] shrink-0" />
            <h2 className="text-[17px] sm:text-[18px] font-bold text-gray-900 leading-tight break-words">Individual Team Member Progress</h2>
          </div>
          <div className="md:hidden space-y-3">
            {teamData.team_progress.length === 0 ? (
              <div className="text-center py-8 text-gray-500 text-sm">No team members assigned yet</div>
            ) : (
              teamData.team_progress.map((member) => (
                <div key={member.employee_id} className="rounded-xl border border-gray-200 p-3.5 shadow-sm min-w-0">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900 break-words leading-snug">{member.name}</p>
                      <p className="text-xs text-gray-600 mt-1 break-words">{member.job_function || 'No job role'}</p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleViewDetails(member)}
                      className="text-[#0056B3] hover:bg-blue-50 shrink-0 h-9 px-3"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      View
                    </Button>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                    <div className="rounded-lg bg-gray-50 p-2">
                      <p className="text-base font-bold text-gray-900">{member.total_topics}</p>
                      <p className="text-[11px] text-gray-600">Total</p>
                    </div>
                    <div className="rounded-lg bg-green-50 p-2">
                      <p className="text-base font-bold text-green-700">{member.completed_topics}</p>
                      <p className="text-[11px] text-gray-600">Done</p>
                    </div>
                    <div className="rounded-lg bg-blue-50 p-2">
                      <p className="text-base font-bold text-blue-700">{member.in_progress_topics}</p>
                      <p className="text-[11px] text-gray-600">Progress</p>
                    </div>
                  </div>
                  <div className="mt-4">
                    <div className="flex items-center justify-between gap-3 text-sm">
                      <span className="text-gray-600">Completion</span>
                      <span className="font-semibold text-gray-900">{Math.round(member.average_completion)}%</span>
                    </div>
                    <div className="mt-2 bg-gray-200 rounded-full h-2.5 w-full overflow-hidden">
                      <div
                        className="bg-[#0056B3] h-2.5 rounded-full"
                        style={{ width: `${Math.round(member.average_completion)}%` }}
                      ></div>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center justify-between gap-3 text-xs text-gray-600">
                    <span>Not Started: {member.untapped_topics}</span>
                    <span className="text-right">Last: {formatDate(member.last_activity)}</span>
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="hidden md:block overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Job Role</TableHead>
                  <TableHead className="text-center">Total Chapters</TableHead>
                  <TableHead className="text-center">Completed</TableHead>
                  <TableHead className="text-center">In Progress</TableHead>
                  <TableHead className="text-center">Not Started</TableHead>
                  <TableHead>Completion %</TableHead>
                  <TableHead>Last Activity</TableHead>
                  <TableHead className="text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {teamData.team_progress.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-gray-500">
                      No team members assigned yet
                    </TableCell>
                  </TableRow>
                ) : (
                  teamData.team_progress.map((member) => (
                    <TableRow key={member.employee_id}>
                      <TableCell className="font-medium">{member.name}</TableCell>
                      <TableCell className="text-sm text-gray-600">{member.job_function}</TableCell>
                      <TableCell className="text-center">{member.total_topics}</TableCell>
                      <TableCell className="text-center">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {member.completed_topics}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {member.in_progress_topics}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {member.untapped_topics}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <div className="flex-1 bg-gray-200 rounded-full h-2 w-20">
                            <div
                              className="bg-[#0056B3] h-2 rounded-full"
                              style={{ width: `${Math.round(member.average_completion)}%` }}
                            ></div>
                          </div>
                          <span className="text-sm font-medium">{Math.round(member.average_completion)}%</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-gray-600">
                        {formatDate(member.last_activity)}
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleViewDetails(member)}
                          className="text-[#0056B3] hover:bg-blue-50"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
        
        {/* Areas of Strength & Improvement - MATCHING STAFF PROFILE UI */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-5 md:mb-8">
          {/* Areas of Strength */}
          <Card className="p-4 sm:p-6 min-w-0">
            <div className="flex items-center gap-3 mb-4 min-w-0">
              <TrendingUp className="w-6 h-6 text-green-600 shrink-0" />
              <h2 className="text-lg font-bold text-green-600 break-words">Areas of Strength</h2>
            </div>
            <p className="text-sm text-gray-600 mb-4">Chapters with ≥90% team average score</p>
            
            {teamData.strength_topics && teamData.strength_topics.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {teamData.strength_topics.map((topic, index) => (
                  <div 
                    key={topic.topic_id || index}
                    className="px-3 py-1.5 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors min-w-0"
                  >
                    <span className="text-sm text-gray-900 break-words">{topic.topic_name}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">No strengths identified yet</p>
            )}
          </Card>
          
          {/* Areas of Improvement */}
          <Card className="p-4 sm:p-6 min-w-0">
            <div className="flex items-center gap-3 mb-4 min-w-0">
              <TrendingDown className="w-6 h-6 text-orange-600 shrink-0" />
              <h2 className="text-lg font-bold text-orange-600 break-words">Areas for Improvement</h2>
            </div>
            <p className="text-sm text-gray-600 mb-4">Chapters with ≤60% team average score</p>
            
            {teamData.improvement_topics && teamData.improvement_topics.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {teamData.improvement_topics.map((topic, index) => (
                  <div 
                    key={topic.topic_id || index}
                    className="px-3 py-1.5 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors min-w-0"
                  >
                    <span className="text-sm text-gray-900 break-words">{topic.topic_name}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">No improvement areas identified</p>
            )}
          </Card>
        </div>
        
        {/* Topic-wise Team Performance Table */}
        <div className="bg-white rounded-xl shadow-md p-4 sm:p-5 lg:p-6 mb-5 md:mb-8 max-w-full overflow-hidden">
          <div className="flex items-center gap-3 mb-4 md:mb-6 min-w-0">
            <BookOpen className="w-6 h-6 text-[#0056B3] shrink-0" />
            <h2 className="text-[17px] sm:text-[18px] font-bold text-gray-900 leading-tight break-words">
              Topic-wise Team Performance - {deptMetrics.total_team_members} Members (including you)
            </h2>
          </div>
          <div>
            {teamData.topic_performance && teamData.topic_performance.length > 0 ? (
              <>
                <div className="md:hidden space-y-3">
                  {teamData.topic_performance.map((topic, index) => (
                    <div key={topic.topic_id || index} className="rounded-xl border border-gray-200 p-3.5 shadow-sm min-w-0">
                      <p className="font-semibold text-gray-900 leading-snug break-words">{topic.topic_name}</p>
                      <div className="mt-3 flex items-center gap-3">
                        <div className="flex-1 bg-gray-200 rounded-full h-2.5 overflow-hidden min-w-0">
                          <div
                            className={`h-2.5 rounded-full ${
                              topic.average_score >= 95 ? 'bg-green-600' :
                              topic.average_score >= 80 ? 'bg-blue-600' :
                              topic.average_score >= 60 ? 'bg-yellow-600' :
                              'bg-orange-600'
                            }`}
                            style={{ width: `${topic.average_score}%` }}
                          ></div>
                        </div>
                        <span className={`text-sm font-semibold shrink-0 ${
                          topic.average_score >= 95 ? 'text-green-700' :
                          topic.average_score >= 80 ? 'text-blue-700' :
                          topic.average_score >= 60 ? 'text-yellow-700' :
                          'text-orange-700'
                        }`}>
                          {topic.average_score}%
                        </span>
                      </div>
                      <div className="mt-3 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {topic.members_completed} completed
                      </div>
                    </div>
                  ))}
                </div>
                <div className="hidden md:block overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Chapter Name</TableHead>
                        <TableHead className="text-center">Average Score (%)</TableHead>
                        <TableHead className="text-center">Total Members Completed</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {teamData.topic_performance.map((topic, index) => (
                        <TableRow key={topic.topic_id || index}>
                          <TableCell className="font-medium">{topic.topic_name}</TableCell>
                          <TableCell className="text-center">
                            <div className="flex items-center justify-center space-x-2">
                              <div className="w-20 bg-gray-200 rounded-full h-2">
                                <div
                                  className={`h-2 rounded-full ${
                                    topic.average_score >= 95 ? 'bg-green-600' :
                                    topic.average_score >= 80 ? 'bg-blue-600' :
                                    topic.average_score >= 60 ? 'bg-yellow-600' :
                                    'bg-orange-600'
                                  }`}
                                  style={{ width: `${topic.average_score}%` }}
                                ></div>
                              </div>
                              <span className={`text-sm font-semibold ${
                                topic.average_score >= 95 ? 'text-green-700' :
                                topic.average_score >= 80 ? 'text-blue-700' :
                                topic.average_score >= 60 ? 'text-yellow-700' :
                                'text-orange-700'
                              }`}>
                                {topic.average_score}%
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              {topic.members_completed}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p className="text-sm">no chapter performance data available yet</p>
              </div>
            )}
          </div>
        </div>
      
      {/* Drill-down Modal */}
      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="w-[calc(100vw-1rem)] sm:w-full max-w-4xl max-h-[90vh] overflow-hidden p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-[22px] leading-tight break-words pr-6" style={{ color: '#0056B3' }}>
              {selectedMember?.name} - Detailed Progress
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto min-h-0 mt-4 max-w-full">
          
          {detailsLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-12 h-12 border-4 border-[#0056B3] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : memberDetails ? (
            <div className="space-y-4 sm:space-y-6 min-w-0">
              {/* Member Info */}
              <Card className="p-4 bg-blue-50 min-w-0">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="min-w-0">
                    <p className="text-sm text-gray-600">Email</p>
                    <p className="font-medium break-words">{memberDetails.subordinate_info.email}</p>
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm text-gray-600">Department</p>
                    <p className="font-medium break-words">{memberDetails.subordinate_info.department}</p>
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm text-gray-600">Job Role</p>
                    <p className="font-medium break-words">{memberDetails.subordinate_info.job_function}</p>
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm text-gray-600">Employee ID</p>
                    <p className="font-medium break-words">{memberDetails.subordinate_info.employee_id}</p>
                  </div>
                </div>
              </Card>
              
              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <p className="text-[22px] font-bold text-green-600">{memberDetails.summary.completed}</p>
                  <p className="text-xs text-gray-600">Completed</p>
                </div>
                <div className="text-center p-3 bg-blue-50 rounded-lg">
                  <p className="text-[22px] font-bold text-blue-600">{memberDetails.summary.in_progress}</p>
                  <p className="text-xs text-gray-600">In Progress</p>
                </div>
                <div className="text-center p-3 bg-purple-50 rounded-lg">
                  <p className="text-[22px] font-bold text-purple-600">{memberDetails.summary.visited}</p>
                  <p className="text-xs text-gray-600">Visited</p>
                </div>
                <div className="text-center p-3 bg-orange-50 rounded-lg">
                  <p className="text-[22px] font-bold text-orange-600">{memberDetails.summary.average_completion}%</p>
                  <p className="text-xs text-gray-600">Avg Completion</p>
                </div>
              </div>
              
              {/* Topics by Status */}
              <div className="space-y-4">
                {/* Completed Topics */}
                {memberDetails.topics_by_status.completed.length > 0 && (
                  <div>
                    <h3 className="text-base sm:text-lg font-semibold mb-3 text-green-700 flex items-center min-w-0">
                      <CheckCircle2 className="w-5 h-5 mr-2 shrink-0" />
                      Completed Topics ({memberDetails.topics_by_status.completed.length})
                    </h3>
                    <div className="space-y-2">
                      {memberDetails.topics_by_status.completed.map((topic, idx) => (
                        <div key={idx} className="p-3 bg-green-50 rounded-lg border border-green-200 min-w-0">
                          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium break-words">{topic.topic_name}</p>
                              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 mt-1 text-sm text-gray-600">
                                <span>Score: {topic.score ? `${topic.score.toFixed(1)}%` : 'N/A'}</span>
                                <span>Completed: {formatDate(topic.completed_at)}</span>
                              </div>
                            </div>
                            <Badge variant="success" className="bg-green-600 text-white self-start shrink-0">100%</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* In Progress Topics */}
                {memberDetails.topics_by_status.in_progress.length > 0 && (
                  <div>
                    <h3 className="text-base sm:text-lg font-semibold mb-3 text-blue-700 flex items-center min-w-0">
                      <Clock className="w-5 h-5 mr-2 shrink-0" />
                      In Progress ({memberDetails.topics_by_status.in_progress.length})
                    </h3>
                    <div className="space-y-2">
                      {memberDetails.topics_by_status.in_progress.map((topic, idx) => (
                        <div key={idx} className="p-3 bg-blue-50 rounded-lg border border-blue-200 min-w-0">
                          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium break-words">{topic.topic_name}</p>
                              <div className="flex items-center gap-4 mt-1 text-sm text-gray-600">
                                <span>Last accessed: {formatDate(topic.last_accessed_at)}</span>
                              </div>
                            </div>
                            <Badge variant="default" className="bg-blue-600 text-white self-start shrink-0">
                              {topic.completion_percentage.toFixed(0)}%
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Visited Topics */}
                {memberDetails.topics_by_status.visited.length > 0 && (
                  <div>
                    <h3 className="text-base sm:text-lg font-semibold mb-3 text-purple-700 flex items-center min-w-0">
                      <Eye className="w-4 h-4 mr-2 shrink-0" />
                      Visited ({memberDetails.topics_by_status.visited.length})
                    </h3>
                    <div className="space-y-2">
                      {memberDetails.topics_by_status.visited.map((topic, idx) => (
                        <div key={idx} className="p-3 bg-purple-50 rounded-lg border border-purple-200 min-w-0">
                          <p className="font-medium break-words">{topic.topic_name}</p>
                          <p className="text-sm text-gray-600 mt-1">
                            First viewed: {formatDate(topic.first_viewed_at)}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Untapped Topics */}
                {memberDetails.topics_by_status.untapped.length > 0 && (
                  <div>
                    <h3 className="text-base sm:text-lg font-semibold mb-3 text-gray-700 flex items-center min-w-0">
                      <AlertCircle className="w-5 h-5 mr-2 shrink-0" />
                      Not Started ({memberDetails.topics_by_status.untapped.length})
                    </h3>
                    <div className="space-y-2">
                      {memberDetails.topics_by_status.untapped.map((topic, idx) => (
                        <div key={idx} className="p-3 bg-gray-50 rounded-lg border border-gray-200 min-w-0">
                          <p className="font-medium text-gray-700 break-words">{topic.topic_name}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
  
  return isEmbedded ? content : <AdminLayout>{content}</AdminLayout>;
};

export default ReportingManagerDashboard;
